/**
 * Analytics composable for standardized PostHog tracking
 */

import funnelStages from '../../../shared/funnel-stages.json'

/**
 * Track a page view
 * @param {string} pageName - Name of the page
 * @param {object} properties - Additional properties
 */
export function trackPage(pageName, properties = {}) {
  if (window.posthog) {
    window.posthog.capture('$pageview', {
      page_name: pageName,
      ...properties
    })
  }
}

/**
 * Track a custom event
 * @param {string} eventName - The event name
 * @param {object} properties - Event properties
 * @param {object} [options] - PostHog capture options. Pass `BEFORE_REDIRECT`
 *   (helpers/partner-return.js) for an event fired right before the page leaves.
 */
export function trackEvent(eventName, properties = {}, options = undefined) {
  if (window.posthog) {
    window.posthog.capture(eventName, properties, options)
  }
}

/**
 * Track a button/CTA click
 * @param {string} elementName - Name of the clicked element
 * @param {string} location - Where the element is located
 * @param {object} extraProps - Additional properties
 */
export function trackClick(elementName, location, extraProps = {}) {
  trackEvent('element_clicked', {
    element_name: elementName,
    location,
    ...extraProps
  })
}

/**
 * Track feature usage
 * @param {string} featureName - Name of the feature
 * @param {object} properties - Additional properties
 */
export function trackFeatureUsage(featureName, properties = {}) {
  trackEvent('feature_used', {
    feature_name: featureName,
    ...properties
  })
}

/**
 * Track form interactions
 * @param {string} formName - Name of the form
 * @param {string} action - Action type (started, submitted, error)
 * @param {object} properties - Additional properties
 */
export function trackForm(formName, action, properties = {}) {
  trackEvent(`form_${action}`, {
    form_name: formName,
    ...properties
  })
}

/**
 * Track a lifecycle milestone: an event plus the person properties that make it
 * queryable as state rather than as event history.
 *
 * Each milestone writes two keys — a boolean saying it happened, and a
 * `first_*_at` timestamp saying when it first did. `$set_once` means PostHog
 * keeps the earliest value and ignores every later write, so this is safe to
 * call on every occurrence and needs no dedupe of its own.
 *
 * The key derivation is deliberately identical to `milestone()` in
 * `api/api/services/posthog.js` and `eve/agent/lib/td-analytics.ts`. All three
 * runtimes write to the same PostHog project, so if the naming drifts the
 * cohorts built on these properties quietly split in two.
 *
 * @param {string} eventName - The event to capture
 * @param {string} attribute - snake_case person attribute, e.g. `viewed_pricing`
 * @param {object} properties - Event properties
 * @param {object} extraSet - Extra person properties to overwrite alongside the flag
 * @param {object} extraSetOnce - Extra first-touch properties, never overwritten
 */
export function trackMilestone(
  eventName,
  attribute,
  properties = {},
  extraSet = {},
  extraSetOnce = {}
) {
  if (!window.posthog) return

  const timestampKey = attribute.startsWith('first_')
    ? `${attribute}_at`
    : `first_${attribute}_at`

  window.posthog.capture(eventName, {
    ...properties,
    $set: { [attribute]: true, ...extraSet },
    $set_once: { [timestampKey]: new Date().toISOString(), ...extraSetOnce }
  })
}

/**
 * A paywall milestone plus the two wall dimensions. Browser-side twin of
 * `paywall()` in `api/api/services/posthog.js` — same property names, same
 * values, because both write to the same PostHog project and the same Attio
 * attributes downstream.
 *
 * The values are Attio option TITLES, not our internal keys: PostHog's Attio
 * destination maps a person property onto an attribute with no transformation,
 * and Attio matches select options by title. A title that hasn't been through
 * `scripts/attio-setup.mjs` is a 400 that nothing surfaces.
 *
 * @param {string} eventName - The event to capture
 * @param {string} attribute - The `paywall_*` person attribute
 * @param {string} reasonKey - A key from `wallReasons` in shared/funnel-stages.json
 * @param {object} properties - Event properties
 */
export function trackPaywall(eventName, attribute, reasonKey, properties = {}) {
  const reason = funnelStages.wallReasons.find((r) => r.key === reasonKey)
  if (!reason) {
    console.warn(`[analytics] unknown wall reason "${reasonKey}"`)
    return trackMilestone(eventName, attribute, properties)
  }
  return trackMilestone(
    eventName,
    attribute,
    properties,
    // Overwritten every time, so whatever it holds at checkout is what sold them.
    { upgrade_trigger: reason.title },
    // The first wall they ever hit. Never overwritten.
    { first_wall_reason: reason.title }
  )
}

/**
 * Identify a user
 * @param {string} userId - User ID
 * @param {object} traits - User traits
 */
export function identify(userId, traits = {}) {
  if (window.posthog) {
    window.posthog.identify(userId, traits)
  }
}

/**
 * Vue composable for analytics
 */
export function useAnalytics() {
  return {
    trackPage,
    trackEvent,
    trackClick,
    trackFeatureUsage,
    trackForm,
    identify
  }
}

export default useAnalytics
